/**
 * Вільна розмова з ІІ-співрозмовником.
 * Додатковий режим поверх сценарних сцен: працює тільки якщо на сервері
 * налаштований ключ. Немає ключа — екран чесно про це каже (див. D-005).
 */
import { useEffect, useRef, useState } from 'react'
import { useStore } from '../lib/store.ts'
import { Speak, TopBar } from '../components/ui.tsx'

interface Msg {
  who: 'you' | 'them'
  pl: string
  uk?: string
  correction?: string | null
}

const SCENES: Record<string, { role: string; setting: string; opener: string; openerUk: string }> = {
  restoran: {
    role: 'офіціант у краківському ресторані',
    setting: 'вечір, ресторан у Казімєжі, гість щойно сів за столик',
    opener: 'Dobry wieczór! Czy podać kartę?',
    openerUk: 'Добрий вечір! Подати меню?',
  },
  gotel: {
    role: 'адміністратор готелю в Ґданську',
    setting: 'гість підходить до стійки реєстрації',
    opener: 'Dzień dobry! W czym mogę pomóc?',
    openerUk: 'Добрий день! Чим можу допомогти?',
  },
  misto: {
    role: 'перехожий на вулиці Кракова',
    setting: 'турист зупинив вас, щоб спитати дорогу',
    opener: 'Tak, słucham pana?',
    openerUk: 'Так, слухаю вас?',
  },
  magazyn: {
    role: 'продавець у магазині',
    setting: 'покупець зайшов і роздивляється полиці',
    opener: 'Dzień dobry, szuka pan czegoś?',
    openerUk: 'Добрий день, щось шукаєте?',
  },
}

export function ChatScreen({ moduleId }: { moduleId?: string }) {
  const { idx } = useStore()
  const sceneKey = moduleId && SCENES[moduleId] ? moduleId : 'misto'
  const scene = SCENES[sceneKey]!
  const [messages, setMessages] = useState<Msg[]>([
    { who: 'them', pl: scene.opener, uk: scene.openerUk },
  ])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [unavailable, setUnavailable] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, busy])

  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    setInput('')
    setMessages((m) => [...m, { who: 'you', pl: text }])
    setBusy(true)
    try {
      const history = messages.slice(-16).map((m) => ({
        role: m.who === 'you' ? ('user' as const) : ('assistant' as const),
        content: m.who === 'you' ? m.pl : JSON.stringify({ pl: m.pl, uk: m.uk ?? '', correction: null }),
      }))
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: scene.role, setting: scene.setting, history, message: text }),
      })
      if (res.status === 503) {
        setUnavailable('Режим вільної розмови поки не налаштований на сервері.')
        return
      }
      if (!res.ok) throw new Error(`Сервер відповів ${res.status}`)
      const data = (await res.json()) as { pl: string; uk: string; correction: string | null }
      setMessages((m) => [...m, { who: 'them', pl: data.pl, uk: data.uk, correction: data.correction }])
    } catch (e) {
      setUnavailable(
        `Не вдалося звʼязатись зі співрозмовником: ${(e as Error).message}. ` +
        'Сценарні рольові сцени працюють без інтернету — спробуй їх.',
      )
    } finally {
      setBusy(false)
    }
  }

  const module = moduleId ? idx.moduleById.get(moduleId) : undefined

  return (
    <>
      <TopBar title="Вільна розмова" />
      <div className="screen stack">
        <div className="card stack--tight stack">
          <div className="row">
            <span style={{ fontSize: 26 }} aria-hidden>🗣️</span>
            <div>
              <div style={{ fontWeight: 600 }}>{scene.role}</div>
              <p className="small muted">{scene.setting}</p>
            </div>
          </div>
          <p className="tiny faint">
            Пиши польською як умієш. Співрозмовник відповідатиме просто і мʼяко виправлятиме
            помилки українською. Помилятись тут — і є сенс вправи.
          </p>
        </div>

        <div className="stack">
          {messages.map((m, i) => (
            <div className={`line${m.who === 'you' ? ' line--you' : ''}`} key={i}>
              {m.who === 'them' && <Speak text={m.pl} small />}
              <div className="bubble">
                <div className="bubble__pl">{m.pl}</div>
                {m.uk && <div className="bubble__uk">{m.uk}</div>}
                {m.correction && (
                  <div className="feedback feedback--hint" style={{ marginTop: 8 }}>✏️ {m.correction}</div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="line">
              <div className="bubble"><div className="spinner" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {unavailable ? (
          <div className="card stack--tight stack">
            <p className="small">{unavailable}</p>
            {module && module.roleplays[0] && (
              <a className="btn btn--wide" href={`#/roleplay/${module.id}/${module.roleplays[0].id}`}>
                Відкрити сценарну сцену
              </a>
            )}
            <a className="btn btn--ghost btn--wide" href="#/modules">До модулів</a>
          </div>
        ) : (
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Напиши польською…"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              disabled={busy}
            />
            <button className="btn btn--primary" style={{ padding: '0 18px' }} disabled={busy || !input.trim()} onClick={send}>
              ➤
            </button>
          </div>
        )}
      </div>
    </>
  )
}
